/**
 * Language Utility functions for handling language preferences
 * @author shabars
 */

/**
 * Determines the preferred language for the application based on the following priority:
 * 1. User's saved language preference in localStorage
 * 2. User's browser/device language setting
 * 3. Falls back to English ('en') if neither of the above is available or supported
 *
 * @returns {string} The two-letter language code for the preferred language
 *
 * @example
 * // If user has previously selected Spanish
 * localStorage.getItem('userLanguagePreference') === 'es'
 * getPreferredLanguage() // Returns 'es'
 *
 * @example
 * // If no saved preference, but browser is set to French
 * navigator.language === 'fr-FR'
 * getPreferredLanguage() // Returns 'fr'
 *
 * @example
 * // If browser language is not supported
 * navigator.language === 'ru-RU'
 * getPreferredLanguage() // Returns 'en'
 */
export const getPreferredLanguage = () => {
    const savedLanguage = localStorage.getItem('userLanguagePreference');
    if (savedLanguage) {
        return savedLanguage;
    }

    if (navigator && navigator.language) {
        const deviceLanguage = navigator.language.split(/[-_]/)[0];
        return supportedLanguages.includes(deviceLanguage) ? deviceLanguage : 'en';
    }
    // Fallback to English if no other option is available
    return 'en';
};

/**
 * Array of supported language codes in the application.
 * @constant {string[]}
 * @default ['en', 'es', 'br', 'jp', 'zh', 'nl', 'fr', 'it', 'de', 'pt']
 *
 * Supported languages:
 * - en: English
 * - es: Spanish
 * - br: Brazilian Portuguese
 * - jp: Japanese
 * - zh: Chinese
 * - nl: Dutch
 * - fr: French
 * - it: Italian
 * - de: German
 * - pt: Portuguese
 */
export const supportedLanguages = ['en', 'es','br', 'jp','zh', 'nl','fr', 'it', 'de', 'pt'];
